import { SelectOption } from '@ssg/common/Helpers/Helpers';

const AddressSearchUrl = 'https://adressevaelger.dk/adresser/soeg';
const AddressLookupUrl = 'https://adressevaelger.dk';
const AddressToken = 'adressevaelger123';

export interface FullAddressObject {
	data: FullAddress;
	forslagstekst: string;
}
interface FullAddress {
	type: 'adresse' | 'husnummer' | 'manual';
	id: string;
	road: string;
	houseNumber: string;
	postalCode: string;
	city: string;
	floor: string;
	door?: string;
}

interface AddressSearchResult {
	type: string;
	id: string;
	titel: string;
	vejnavn?: string;
	husnummer?: string;
}

interface AddressSearchResponse {
	fund?: AddressSearchResult[];
}

interface AddressLookupResponse {
	adresse?: {
		adressebetegnelse?: string;
		etagebetegnelse?: string | null;
		doerbetegnelse?: string | null;
		husnummer?: {
			husnummertekst: string;
			vejnavn: string;
		};
		postnummer?: {
			navn: string;
			postnr: string;
		};
	};
	husnummer?: {
		adgangsadressebetegnelse?: string;
		husnummertekst: string;
		vejnavn: string;
		postnummer?: {
			navn: string;
			postnr: string;
		};
	};
}

export async function addressRequest(addressValue: string): Promise<SelectOption[]> {
	const json: SelectOption[] = [];
	try {
		const addresses = await searchAddresses(addressValue);
		const toAddressObject: SelectOption[] = addresses.map(address => ({
			label: address.data.road,
			value: address.data.road,
		}));
		json.push(...toAddressObject);

		json.push({ label: addressValue, value: addressValue });
	} catch {
		json.push({ label: addressValue, value: addressValue });
	}

	return getUniqueAddresses(json);
}

export async function fullAddressRequest(addressValue: string, skipSelf?: true): Promise<FullAddressObject[]> {
	const json = await searchAddresses(addressValue);

	if (!skipSelf) {
		json.push({
			forslagstekst: addressValue,
			data: {
				type: 'manual',
				road: addressValue,
				door: '',
				floor: '',
				houseNumber: '',
				id: addressValue,
				postalCode: '',
				city: '',
			},
		});
	}

	return json;
}

export function getUniqueFullAddresses(items: FullAddressObject[]): FullAddressObject[] {
	const uniqueAddresses: FullAddressObject[] = [];
	return items.reduce((addresses, current) => {
		if (!addresses.some(address => address.data.id === current.data.id)) {
			addresses.push(current);
		}
		return addresses;
	}, uniqueAddresses);
}

async function searchAddresses(addressValue: string): Promise<FullAddressObject[]> {
	try {
		const searchUrl = new URL(AddressSearchUrl);
		searchUrl.searchParams.set('token', AddressToken);
		searchUrl.searchParams.set('tekst', addressValue);

		const searchResponse = await fetch(searchUrl.toString());
		if (!searchResponse.ok) {
			return [];
		}

		const searchResult: AddressSearchResponse = await searchResponse.json();
		const addressResults = (searchResult.fund ?? []).filter(result => result.type === 'adresse' || result.type === 'husnummer');
		return Promise.all(addressResults.map(result => getAddressById(result)));
	} catch {
		return [];
	}
}

async function getAddressById(result: AddressSearchResult): Promise<FullAddressObject> {
	let lookupResult: AddressLookupResponse = {};
	try {
		const lookupType = result.type === 'adresse' ? 'adresser' : 'husnumre';
		const lookupUrl = new URL(`${AddressLookupUrl}/${lookupType}/${encodeURIComponent(result.id)}`);
		lookupUrl.searchParams.set('token', AddressToken);

		const lookupResponse = await fetch(lookupUrl.toString());
		if (lookupResponse.ok) {
			lookupResult = await lookupResponse.json();
		}
	} catch {
		lookupResult = {};
	}

	const address = lookupResult.adresse;
	const houseNumber = address?.husnummer ?? lookupResult.husnummer;
	const postalCode = address?.postnummer ?? lookupResult.husnummer?.postnummer;
	const title = address?.adressebetegnelse ?? lookupResult.husnummer?.adgangsadressebetegnelse ?? result.titel;
	const parsedTitle = parseAddressTitle(title);

	return {
		forslagstekst: title,
		data: {
			type: result.type === 'adresse' ? 'adresse' : 'husnummer',
			road: houseNumber?.vejnavn ?? result.vejnavn ?? parsedTitle.road,
			door: address?.doerbetegnelse ?? '',
			floor: address?.etagebetegnelse ?? parsedTitle.floor,
			houseNumber: houseNumber?.husnummertekst ?? result.husnummer ?? parsedTitle.houseNumber,
			id: result.id,
			postalCode: postalCode?.postnr ?? parsedTitle.postalCode,
			city: postalCode?.navn ?? parsedTitle.city,
		},
	};
}

function parseAddressTitle(title: string): { road: string; houseNumber: string; postalCode: string; city: string; floor: string } {
	const parts = title.split(',').map(part => part.trim());
	const addressPart = parts[0] ?? '';
	const addressSeparator = addressPart.lastIndexOf(' ');
	const road = addressSeparator > 0 ? addressPart.slice(0, addressSeparator) : addressPart;
	const houseNumber = addressSeparator > 0 ? addressPart.slice(addressSeparator + 1) : '';
	const postalPartIndex = parts.findIndex(part => /^\d{4}\s/.test(part));
	const postalPart = parts[postalPartIndex] ?? '';
	const postalMatch = postalPart.match(/^(\d{4})\s+(.+)$/);
	const floorParts = parts.slice(1, postalPartIndex < 0 ? parts.length : postalPartIndex);
	const floor = floorParts.find(part => /^(?:(?:kld|kl|st)\.?|\d+(?:\.|\b)|(?:th|tv|mf|ml))(?=\s|$)/i.test(part)) ?? '';

	return {
		road,
		houseNumber,
		postalCode: postalMatch?.[1] ?? '',
		city: postalMatch?.[2] ?? '',
		floor,
	};
}

const getUniqueAddresses = (items: SelectOption[]) => {
	const emptyArray: SelectOption[] = [];
	return items.reduce((accumulator, current) => {
		if (!accumulator.some(x => x.value === current.value)) {
			accumulator.push(current);
		}
		return accumulator;
	}, emptyArray);
};

